import { useMemo } from "react"

import { Button, Select } from "@productlane/ui"

interface DocsColumnStepProps {
	data: string[][]
	hasHeaders: boolean
	back: () => void
	next: () => void
	titleIndex: number
	setTitleIndex: (i: number) => void
	contentIndex: number
	setContentIndex: (i: number) => void
	summaryIndex: number
	setSummaryIndex: (i: number) => void
	imageIndex: number
	setImageIndex: (i: number) => void
	groupIndex: number
	setGroupIndex: (i: number) => void
	sortedData: string[][]
}

function stripHtml(html: string): string {
	// Remove HTML tags
	const stripped = html.replace(/<[^>]*>/g, "")
	// Decode common HTML entities
	const decoded = stripped
		.replace(/&nbsp;/g, " ")
		.replace(/&amp;/g, "&")
		.replace(/&lt;/g, "<")
		.replace(/&gt;/g, ">")
		.replace(/&quot;/g, '"')
		.replace(/&#39;/g, "'")
	return decoded.trim()
}

export function DocsColumnStep({
	data,
	hasHeaders,
	back,
	next,
	titleIndex,
	setTitleIndex,
	contentIndex,
	setContentIndex,
	summaryIndex,
	setSummaryIndex,
	imageIndex,
	setImageIndex,
	groupIndex,
	setGroupIndex,
	sortedData,
}: DocsColumnStepProps) {
	const headers = useMemo(() => {
		if (!data[0]) {
			return []
		}
		const options = hasHeaders
			? data[0].map((it, i) => ({
					label: it,
					value: i.toString(),
				}))
			: data[0].map((_, i) => ({
					label: `${i + 1}. Column`,
					value: i.toString(),
				}))

		return [{ label: "None", value: "-1" }, ...options]
	}, [hasHeaders, data])

	const groupHeaders = useMemo(() => {
		if (!data[0]) {
			return []
		}
		const options = hasHeaders
			? data[0].map((it, i) => ({
					label: it,
					value: i.toString(),
				}))
			: data[0].map((_, i) => ({
					label: `${i + 1}. Column`,
					value: i.toString(),
				}))

		return [
			{ label: "None (use article title as group)", value: "-1" },
			...options,
		]
	}, [hasHeaders, data])

	const uniqueGroups = useMemo(() => {
		if (groupIndex === -1 || !sortedData[groupIndex]) {
			return []
		}
		const groupValues = sortedData[groupIndex].slice(hasHeaders ? 1 : 0)
		return Array.from(new Set(groupValues)).filter((v) => v.trim() !== "")
	}, [groupIndex, sortedData, hasHeaders])

	return (
		<div className="space-y-4">
			<Select
				value={titleIndex.toString()}
				name="titleIndex"
				label="Select the column with the title (required)"
				options={headers.filter((h) => h.value !== "-1")}
				onChange={(it) => setTitleIndex(Number(it))}
				className="px-[1px]"
			/>

			<Select
				value={contentIndex.toString()}
				name="contentIndex"
				label="Select the column with the content (optional)"
				options={headers}
				onChange={(it) => setContentIndex(Number(it))}
				className="px-[1px]"
			/>

			<Select
				value={summaryIndex.toString()}
				name="summaryIndex"
				label="Select the column with the summary (optional)"
				options={headers}
				onChange={(it) => setSummaryIndex(Number(it))}
				className="px-[1px]"
			/>

			<Select
				value={imageIndex.toString()}
				name="imageIndex"
				label="Select the column with the image URL (optional)"
				options={headers}
				onChange={(it) => setImageIndex(Number(it))}
				className="px-[1px]"
			/>

			<Select
				value={groupIndex.toString()}
				name="groupIndex"
				label="Select the column with the group name (optional)"
				options={groupHeaders}
				onChange={(it) => setGroupIndex(Number(it))}
				className="px-[1px]"
			/>

			{groupIndex === -1 ? (
				<div className="rounded-lg border bg-blue-50 p-3 text-xs text-secondary dark:bg-blue-950">
					Each article will be placed in its own group using the article title
					as the group name.
				</div>
			) : (
				<>
					<div className="ml-2 text-xs text-secondary">
						Preview of unique groups ({uniqueGroups.length} total):
					</div>
					<div className="relative mt-1 overflow-hidden rounded-lg border bg-white p-2 dark:bg-gray-900">
						{uniqueGroups.slice(0, 10).map((group, i) => (
							<div className="border-b p-1 text-secondary" key={i}>
								{group}
							</div>
						))}
						{uniqueGroups.length > 10 && (
							<div className="p-1 text-xs text-tertiary">
								... and {uniqueGroups.length - 10} more
							</div>
						)}
						<div className="absolute inset-x-0 bottom-0 h-[60%] bg-gradient-to-b from-transparent via-white/80 to-white dark:via-brand-900/80 dark:to-brand-900" />
					</div>
				</>
			)}

			<div className="mb-2 mt-4 text-sm text-primary">Preview:</div>
			<div className="relative mt-1 overflow-hidden rounded-lg border bg-white p-2 dark:bg-gray-900">
				{sortedData[titleIndex]
					?.slice(hasHeaders ? 1 : 0, hasHeaders ? 5 : 4)
					.map((it, i) => {
						const rawContent =
							sortedData[contentIndex]?.[hasHeaders ? i + 1 : i]
						const cleanContent = rawContent ? stripHtml(rawContent) : ""

						const rawSummary =
							sortedData[summaryIndex]?.[hasHeaders ? i + 1 : i]
						const cleanSummary = rawSummary ? stripHtml(rawSummary) : ""

						return (
							<div className="border-b p-1 text-secondary" key={i}>
								<div>
									<strong>Title:</strong> {it}
								</div>
								{summaryIndex !== -1 && cleanSummary && (
									<div className="mt-1">
										<strong>Summary:</strong>{" "}
										<span className="line-clamp-1">{cleanSummary}</span>
									</div>
								)}
								{contentIndex !== -1 && cleanContent && (
									<div className="mt-1">
										<strong>Content:</strong>{" "}
										<span className="line-clamp-2">{cleanContent}</span>
									</div>
								)}
								{imageIndex !== -1 && (
									<div className="mt-1 truncate text-xs">
										<strong>Image:</strong>{" "}
										{sortedData[imageIndex]?.[hasHeaders ? i + 1 : i]}
									</div>
								)}
							</div>
						)
					})}
				<div className="absolute inset-x-0 bottom-0 h-[80%] bg-gradient-to-b from-transparent via-white/80 to-white dark:via-brand-900/80 dark:to-brand-900" />
			</div>

			<div className="mt-2 flex justify-between">
				<Button variant="ghost" onClick={back}>
					Back
				</Button>
				<Button onClick={next}>Next</Button>
			</div>
		</div>
	)
}
